import axios from 'axios';
import JSZip from 'jszip';

/** Set in frontend/.env  */
function resolveApiBaseUrl() {
  const url = (process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');
  if (!url) {
    throw new Error(
      'REACT_APP_API_URL is not set. Add it to frontend/.env (see .env.example), then restart: cd frontend && npm start'
    );
  }
  return url;
}

export const API_BASE_URL = resolveApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
});

const TOKEN_KEY = 'fdp_token';
export const AUTH_EXPIRED_EVENT = 'fdp-auth-expired';

export function getStoredToken() {
  return sessionStorage.getItem(TOKEN_KEY);
}

function setStoredToken(token) {
  sessionStorage.setItem(TOKEN_KEY, token);
}

function clearStoredToken() {
  sessionStorage.removeItem(TOKEN_KEY);
}

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    if (status === 401 && detail === 'Not authenticated') {
      clearStoredToken();
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    }
    return Promise.reject(error);
  },
);


// Keep in sync with backend/output_names.py (all 4 formats share duplicate rules)
// image(2).png → image(2)_standard | _watermarked | _masked | _all.pdf
const OUTPUT_SUFFIX = {
  standard: 'standard',
  watermark: 'watermarked',
  masked: 'masked',
  all: 'all',
};

/** Bulk ZIP download name, e.g. updated_watermarked.zip */
export function bulkZipFilename(outputType) {
  const suffix = OUTPUT_SUFFIX[outputType] || outputType;
  return `updated_${suffix}.zip`;
}

function parseBatchManifestHeader(headerValue) {
  if (!headerValue || typeof headerValue !== 'string') return null;
  try {
    const json = atob(headerValue.trim());
    const parsed = JSON.parse(json);
    if (parsed && Array.isArray(parsed.items)) return parsed;
  } catch {
    return null;
  }
  return null;
}

/** Prefer manifest with items; poll JSON beats truncated/missing ZIP response headers. */
function resolveBatchManifest(primary, fallback) {
  if (primary?.items?.length) return primary;
  if (fallback?.items?.length) return fallback;
  return primary || fallback || null;
}

function readBatchManifestHeader(headers) {
  if (!headers) return null;
  const raw =
    headers['x-batch-manifest']
    || headers['X-Batch-Manifest']
    || headers['X-BATCH-MANIFEST'];
  return parseBatchManifestHeader(raw);
}

/** Upload basename only - same rules as backend/output_names.py upload_basename */
export function uploadBasename(uploadName) {
  const base = (uploadName || 'document').replace(/\\/g, '/').split('/').pop()
    .replace(/\//g, '_').replace(/\\/g, '_');
  return base || 'document';
}

/** Processed PDF name derived from an original/upload basename */
export function processedFilenameFromOriginal(originalName, outputType) {
  const suffix = OUTPUT_SUFFIX[outputType] || outputType;
  const base = uploadBasename(originalName);
  const dot = base.lastIndexOf('.');
  const stem = dot > 0 ? base.slice(0, dot) : base;
  return `${stem}_${suffix}.pdf`;
}

/** Fallback processed name from upload (no duplicate numbering - use manifest when available) */
export function outputPdfFilename(uploadName, outputType) {
  return processedFilenameFromOriginal(uploadName, outputType);
}

/** Ensure PDF /Title metadata matches the download filename (fixes UUID titles in browser viewers). */
export async function pdfBlobWithDisplayName(blob, filename) {
  const name = filename || 'document_standard.pdf';
  try {
    const { PDFDocument } = await import('pdf-lib');
    const doc = await PDFDocument.load(await blob.arrayBuffer());
    doc.setTitle(name);
    doc.setSubject(name);
    const bytes = await doc.save();
    return new File([bytes], name, { type: 'application/pdf' });
  } catch {
    return new File([blob], name, { type: 'application/pdf' });
  }
}

/** Object URL for inline preview with correct document title in the embedded viewer. */
export async function createPdfPreviewUrl(blob, filename) {
  const file = await pdfBlobWithDisplayName(blob, filename);
  return URL.createObjectURL(file);
}

function filenameFromContentDisposition(header) {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      return null;
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
}

export async function login(username, password) {
  const res = await api.post('/login', {username, password});
  const token = res.data?.token;
  if (!token) throw new Error('No token returned from server');
  setStoredToken(token);
  return token;
}

export async function logout() {
  try {
    await abortAllServerJobs();
    await api.post('/logout');
  } catch {
    // clear token regardless
  }
  clearStoredToken();
  clearActiveBulkJob();
}

export async function cancelAllBulkJobs({ signal } = {}) {
  const res = await api.post('/bulk-jobs/cancel-all', null, { timeout: 60000, signal });
  clearActiveBulkJob();
  return res.data;
}

/** Stop every server bulk job and clear local session state (fire-and-forget safe). */
export async function abortAllServerJobs({ signal } = {}) {
  clearActiveBulkJob();
  try {
    await api.post('/bulk-jobs/cancel-all', null, { timeout: 30000, signal });
  } catch {
    // User may already be logged out or the server may be unreachable.
  }
}

async function detailFromErrorResponse(error) {
  const data = error?.response?.data;
  if (!data) return null;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed?.detail ?? null;
    } catch {
      return data;
    }
  }
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      return parsed?.detail ?? text;
    } catch {
      try {
        return await data.text();
      } catch {
        return null;
      }
    }
  }
  return data?.detail ?? null;
}

/**
 * Generate final PDF with given output type.
 * outputType: "standard" | "watermark" | "masked" | "all"
 * Optional extracted + summary reuse data (faster, avoids duplicate AI calls).
 * Returns: { blob, filename }
 */
export async function generatePDF(file, outputType, { extracted, summary } = {}) {
  const form = new FormData();
  form.append('file', file);
  form.append('output_type', outputType);
  if (extracted && typeof extracted === 'object') {
    form.append('extracted_json', JSON.stringify(extracted));
  }
  if (summary && typeof summary === 'string') {
    form.append('summary_text', summary);
  }
  try {
    const res = await api.post('/generate-pdf', form, {
      responseType: 'blob',
    });
    const fromHeader = filenameFromContentDisposition(res.headers['content-disposition']);
    const filename = fromHeader || outputPdfFilename(file?.name, outputType);
    return { blob: res.data, filename };
  } catch (err) {
    const detail = await detailFromErrorResponse(err);
    if (detail) {
      const message = typeof detail === 'string' ? detail : JSON.stringify(detail);
      throw new Error(message);
    }
    throw err;
  }
}

/** PDF arcname inside a bulk ZIP for a manifest item. */
export function manifestPdfArcname(item) {
  if (!item || typeof item !== 'object') return null;
  const name = item.output_filename;
  return typeof name === 'string' && name.trim() ? name.trim() : null;
}

/** Manifest rows that produced a processed PDF. */
export function manifestSuccessItems(items) {
  return (Array.isArray(items) ? items : []).filter(
    (item) => item && manifestPdfArcname(item) && item.extracted,
  );
}

/** Manifest rows that failed during bulk processing. */
export function manifestFailedItems(items) {
  return (Array.isArray(items) ? items : []).filter((item) => item?.error);
}

/** Poll manifest may be slim - review needs spilled rows with extracted/summary. */
export function manifestItemsNeedFullDetail(items) {
  return (Array.isArray(items) ? items : []).some(
    (item) => item?.output_filename && !item?.extracted,
  );
}

export async function ensureFullManifestItems(jobId, items, { signal } = {}) {
  const list = Array.isArray(items) ? items : [];
  if (!jobId || !manifestItemsNeedFullDetail(list)) {
    return list;
  }
  try {
    const full = await getBulkJobManifest(jobId, { signal });
    return full?.items?.length ? full.items : list;
  } catch {
    return list;
  }
}

/** At least one manifest row has extracted field data for the review UI. */
export function manifestItemsHaveReviewData(items) {
  return (Array.isArray(items) ? items : []).some(
    (item) => item?.extracted && typeof item.extracted === 'object',
  );
}

/** Normalize async job API result (manifest may be nested or flat). */
export function normalizeAsyncJobResult(result) {
  if (!result || typeof result !== 'object') {
    return { jobId: null, manifest: null, zipFilename: null, blob: null };
  }
  const manifest = result.manifest?.items
    ? result.manifest
    : (Array.isArray(result.manifest) ? { items: result.manifest } : result.manifest);
  return {
    jobId: result.jobId || result.job_id || null,
    manifest,
    zipFilename: result.zipFilename || result.zip_filename || null,
    blob: result.blob ?? null,
  };
}

/** Per-file Zoho save summary for bulk batches. */
export function summarizeBulkZohoStatus(items) {
  const list = Array.isArray(items) ? items : [];
  const processed = list.filter((item) => item?.output_filename);
  const processFailed = manifestFailedItems(list);
  const zohoFailed = processed.filter((item) => item.zoho_error);
  const zohoSaved = processed.filter((item) => !item.zoho_error);
  const sheetSkipped = zohoSaved.filter((item) => item.zoho?.sheet_action === 'skipped');
  const sheetNew = zohoSaved.filter(
    (item) => item.zoho?.sheet_action === 'appended' || item.zoho?.sheet_action === 'updated',
  );
  return {
    total: list.length,
    processed: processed.length,
    processFailed,
    zohoFailed,
    zohoSaved,
    sheetSkipped,
    sheetNew,
  };
}

export function formatBulkZohoSummary(stats) {
  if (!stats) return '';
  const parts = [`${stats.zohoSaved.length}/${stats.total} saved to Zoho WorkDrive`];
  if (stats.sheetNew.length > 0) {
    parts.push(`${stats.sheetNew.length} sheet row(s) added/updated`);
  }
  if (stats.sheetSkipped.length > 0) {
    const names = stats.sheetSkipped.map((item) => item.filename || 'Unknown file');
    if (names.length === 1) {
      parts.push(`1 duplicate sheet row skipped (${names[0]})`);
    } else {
      const preview = names.slice(0, 3).join(', ');
      const more = names.length > 3 ? ` +${names.length - 3} more` : '';
      parts.push(`${names.length} duplicate sheet row(s) skipped (${preview}${more})`);
    }
  }
  if (stats.processFailed.length > 0) {
    parts.push(`${stats.processFailed.length} failed to process`);
  }
  if (stats.zohoFailed.length > 0) {
    parts.push(`${stats.zohoFailed.length} Zoho upload failed`);
  }
  return parts.join(' · ');
}

export async function loadZipFromBlob(zipBlob) {
  return JSZip.loadAsync(await zipBlob.arrayBuffer());
}

/** Read one PDF entry from an already-loaded JSZip instance. */
export async function readPdfFromZip(zip, arcname) {
  if (!zip || !arcname) return null;
  const entry = zip.file(arcname) || zip.file(arcname.replace(/\\/g, '/'));
  if (!entry) return null;
  const blob = await entry.async('blob');
  return blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
}

const POLL_INTERVAL_MS = 2000;
/** Large batches can run up to 1.5 hours before the server force-stops them. */
const MAX_PROCESSING_MS = 90 * 60 * 1000;
const POLL_NETWORK_GRACE_MS = 30 * 60 * 1000;
const BULK_JOB_STORAGE_KEY = 'fdp_active_bulk_job';
const ZIP_DOWNLOAD_TIMEOUT_MS = 30 * 60 * 1000;

export class ProcessingCancelledError extends Error {
  constructor(message = 'Processing cancelled') {
    super(message);
    this.name = 'ProcessingCancelledError';
  }
}

export function isProcessingCancelled(err) {
  return (
    err instanceof ProcessingCancelledError
    || err?.name === 'ProcessingCancelledError'
    || err?.code === 'ERR_CANCELED'
    || err?.name === 'CanceledError'
    || err?.message === 'canceled'
  );
}

function throwIfAborted(signal) {
  if (signal?.aborted) {
    throw new ProcessingCancelledError();
  }
}

function sleep(ms, signal) {
  if (!signal) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new ProcessingCancelledError());
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
      reject(new ProcessingCancelledError());
    };
    signal.addEventListener('abort', onAbort);
  });
}

export function getActiveBulkJob() {
  try {
    const raw = sessionStorage.getItem(BULK_JOB_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.jobId && parsed?.total) return parsed;
  } catch {
    // ignore
  }
  return null;
}

export function persistActiveBulkJob(job) {
  sessionStorage.setItem(BULK_JOB_STORAGE_KEY, JSON.stringify(job));
}

export function clearActiveBulkJob() {
  sessionStorage.removeItem(BULK_JOB_STORAGE_KEY);
}

async function withRetries(fn, { attempts = 3, delayMs = 2000, label = 'request', signal } = {}) {
  let lastErr;
  for (let i = 1; i <= attempts; i += 1) {
    throwIfAborted(signal);
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (isProcessingCancelled(err)) throw err;
      const retryable = !err.response || err.response.status >= 500 || err.code === 'ERR_NETWORK';
      if (!retryable || i >= attempts) break;
      await sleep(delayMs * i, signal);
    }
  }
  throw lastErr;
}

export function formatClientApiError(err) {
  const base = err?.message || 'Unexpected error.';
  if (err?.response?.status === 413) {
    return 'Upload too large for the server. Try fewer or smaller files.';
  }
  if (err?.code === 'ECONNABORTED' || /timeout/i.test(base)) {
    return `Request timed out (${API_BASE_URL}). Try uploading again.`;
  }
  if (!err?.response && (err?.code === 'ERR_NETWORK' || /network error/i.test(base))) {
    return `Cannot reach ${API_BASE_URL}. Check HTTPS (use https:// for the API if the site uses HTTPS), CORS_ORIGINS on the backend, and that PM2 is running.`;
  }
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  return base;
}

async function pollBulkJobUntilDone(jobId, { onProgress, signal, startedAt } = {}) {
  let networkDownSince = null;
  const pollStartedAt = startedAt ?? Date.now();

  while (true) {
    throwIfAborted(signal);
    if (Date.now() - pollStartedAt > MAX_PROCESSING_MS) {
      await abortAllServerJobs({ signal });
      throw new Error(
        'Processing took too long (over 1.5 hours). Please try again with fewer files.',
      );
    }

    let status;
    try {
      status = await getBulkJobStatus(jobId, { signal });
      networkDownSince = null;
    } catch (err) {
      if (isProcessingCancelled(err)) {
        clearActiveBulkJob();
        throw err;
      }
      if (err.response?.status === 404) {
        // Job was deleted server-side (usually a duplicate upload request cancelled it).
        try {
          const active = await getActiveBulkJobOnServer({ signal });
          if (
            active?.active
            && active.job_id
            && (active.status === 'queued' || active.status === 'running')
          ) {
            persistActiveBulkJob({
              jobId: active.job_id,
              total: active.total,
              outputType: getActiveBulkJob()?.outputType,
            });
            jobId = active.job_id;
            onProgress?.(active);
            await sleep(POLL_INTERVAL_MS, signal);
            continue;
          }
        } catch {
          // fall through to hard error
        }
        clearActiveBulkJob();
        throw new Error(
          'Processing was interrupted on the server (the job was removed while still running). '
          + 'Please upload again - this is fixed in the latest deploy.',
        );
      }
      const status = err.response?.status;
      const transient =
        err.code === 'ERR_NETWORK'
        || err.code === 'ECONNABORTED'
        || !err.response
        || status === 502
        || status === 503
        || status === 504;
      if (transient) {
        if (!networkDownSince) networkDownSince = Date.now();
        if (Date.now() - networkDownSince < POLL_NETWORK_GRACE_MS) {
          await sleep(POLL_INTERVAL_MS * 2, signal);
          continue;
        }
        throw new Error(formatClientApiError(err));
      }
      throw new Error(formatClientApiError(err));
    }

    onProgress?.(status);

    if (status.status === 'cancelled') {
      await abortAllServerJobs({ signal });
      throw new ProcessingCancelledError(status.error || 'Processing cancelled.');
    }

    if (status.status === 'complete') {
      throwIfAborted(signal);
      if (!status.zip_ready) {
        throw new Error('Batch finished but ZIP is not ready.');
      }
      const manifestFromPoll = await getBulkJobManifest(jobId, { signal });
      const zipFilename = bulkZipFilename(manifestFromPoll?.output_type);
      const stored = getActiveBulkJob();
      persistActiveBulkJob({
        jobId,
        total: status.total,
        outputType: manifestFromPoll?.output_type || stored?.outputType,
        zipPending: true,
        review: true,
        bulk: stored?.bulk,
      });
      onProgress?.({ ...status, processed: status.total, manifest: manifestFromPoll });
      return {
        blob: null,
        jobId,
        manifest: manifestFromPoll,
        zipFilename,
      };
    }

    if (status.status === 'failed' || status.status === 'interrupted') {
      clearActiveBulkJob();
      throw new Error(status.error || 'Processing failed.');
    }

    await sleep(POLL_INTERVAL_MS, signal);
  }
}

export async function resumeBulkJob(jobId, { onProgress, signal } = {}) {
  return pollBulkJobUntilDone(jobId, { onProgress, signal });
}

export async function startBulkJob(files, outputType, { signal } = {}) {
  const form = new FormData();
  files.forEach((f) => form.append('files', f));
  form.append('output_type', outputType);

  const postBulkJob = () => api.post('/bulk-jobs', form, { timeout: 600000, signal });

  const adoptActiveJob = async () => {
    const active = await getActiveBulkJobOnServer({ signal });
    if (
      active?.active
      && active.job_id
      && (active.status === 'queued' || active.status === 'running')
    ) {
      return { jobId: active.job_id, total: active.total };
    }
    return null;
  };

  try {
    const res = await postBulkJob();
    return { jobId: res.data.job_id, total: res.data.total };
  } catch (err) {
    if (isProcessingCancelled(err)) throw err;

    const adopted = await adoptActiveJob();
    if (adopted) return adopted;

    if (err.response?.status !== 429) {
      throw err;
    }

    await cancelAllBulkJobs({ signal });
    const res = await postBulkJob();
    return { jobId: res.data.job_id, total: res.data.total };
  }
}

export async function getBulkJobStatus(jobId, { full = false, signal } = {}) {
  const res = await api.get(`/bulk-jobs/${jobId}`, {
    timeout: 60000,
    params: full ? { full: 1 } : {},
    signal,
  });
  return res.data;
}

export async function getActiveBulkJobOnServer({ signal } = {}) {
  const res = await api.get('/bulk-jobs/active', { timeout: 60000, signal });
  return res.data;
}

export async function getBulkJobManifest(jobId, { signal } = {}) {
  const res = await api.get(`/bulk-jobs/${jobId}/manifest`, { timeout: 120000, signal });
  return res.data;
}

export async function cancelBulkJob(jobId, { signal } = {}) {
  const res = await api.post(`/bulk-jobs/${jobId}/cancel`, null, { timeout: 60000, signal });
  clearActiveBulkJob();
  return res.data;
}

export async function downloadBulkJobZip(jobId, fallbackManifest = null) {
  const res = await withRetries(
    () => api.get(`/bulk-jobs/${jobId}/zip`, {
      responseType: 'arraybuffer',
      timeout: ZIP_DOWNLOAD_TIMEOUT_MS,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    }),
    { attempts: 3, delayMs: 3000, label: 'bulk ZIP download' },
  );
  const blob = new Blob([res.data], { type: 'application/zip' });
  const manifest = resolveBatchManifest(
    readBatchManifestHeader(res.headers),
    fallbackManifest,
  );
  const zipFilename =
    filenameFromContentDisposition(res.headers['content-disposition'])
    || bulkZipFilename(manifest?.output_type || fallbackManifest?.output_type);
  return { blob, manifest, zipFilename };
}

/** Fetch one processed PDF from a completed batch (preview without full ZIP download). */
export async function fetchBulkJobPdf(jobId, arcname) {
  if (!jobId || !arcname) {
    throw new Error('Batch PDF preview is not available.');
  }
  try {
    const res = await api.get(
      `/bulk-jobs/${jobId}/pdf/${encodeURIComponent(arcname)}`,
      { responseType: 'blob', timeout: 120000 },
    );
    const blob = res.data;
    return blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
  } catch (err) {
    if (err.response?.status === 404) {
      throw new Error(
        'PDF not found for this batch on the server. Try Download ZIP or re-process the batch.',
      );
    }
    if (err.code === 'ERR_NETWORK' || !err.response) {
      throw new Error('PDF preview failed (connection dropped). Try again.');
    }
    const detail = err?.response?.data?.detail;
    throw new Error(typeof detail === 'string' ? detail : 'PDF preview failed.');
  }
}

/** Download batch ZIP on demand (export). Retries transient failures. */
export async function fetchBulkJobZip(jobId, fallbackManifest = null) {
  if (!jobId) {
    throw new Error('Batch ZIP is not available - process the batch again.');
  }
  try {
    return await downloadBulkJobZip(jobId, fallbackManifest);
  } catch (err) {
    if (err.response?.status === 404) {
      throw new Error(
        'Batch ZIP no longer on the server (expired or already cleared). '
        + 'Your files are still in Zoho - re-process only if you need a new ZIP.',
      );
    }
    if (err.code === 'ERR_NETWORK' || !err.response) {
      throw new Error(
        'ZIP download failed (connection dropped). Files are already saved to Zoho. Try preview again in a moment.',
      );
    }
    throw err;
  }
}

async function bulkPackZipSync(files, outputType, { signal } = {}) {
  const form = new FormData();
  files.forEach((f) => form.append('files', f));
  form.append('output_type', outputType);
  const res = await api.post('/bulk-pack-zip', form, {
    responseType: 'arraybuffer',
    timeout: 600000,
    signal,
  });
  const buf = res.data;
  const blob = new Blob([buf], { type: 'application/zip' });
  const manifest = readBatchManifestHeader(res.headers);
  const zipFilename =
    filenameFromContentDisposition(res.headers['content-disposition'])
    || bulkZipFilename(outputType);
  return { blob, manifest, zipFilename };
}

async function bulkPackZipAsync(files, outputType, { onProgress, signal, bulk } = {}) {
  const { jobId, total } = await startBulkJob(files, outputType, { signal });
  persistActiveBulkJob({ jobId, total, outputType, bulk: Boolean(bulk) });
  onProgress?.({
    status: 'queued',
    job_id: jobId,
    processed: 0,
    total,
    current_filename: null,
  });
  return pollBulkJobUntilDone(jobId, { onProgress, signal });
}

/**
 * Process multiple documents → one ZIP (one processed PDF per input file).
 * Supports PDF, PNG, Word (.doc/.docx), and EML with any output format
 * (standard | watermark | masked | all).
 * Bulk mode uses async jobs with progress polling (no long HTTP timeout).
 * Pass jobId to resume polling an in-flight server job (e.g. after page refresh).
 */
export async function bulkPackZip(files, outputType, { onProgress, jobId, useAsyncJob, signal, bulk } = {}) {
  if (jobId) {
    return pollBulkJobUntilDone(jobId, { onProgress, signal });
  }
  if (useAsyncJob || files.length > 1) {
    return bulkPackZipAsync(files, outputType, { onProgress, signal, bulk });
  }
  return bulkPackZipSync(files, outputType, { signal });
}